import { useEffect, useMemo, useState } from 'react'
import useLocalStorage from './useLocalStorage'
import { ThemeOption } from '../context/GlobalContext'
import { isOfficeInitialized } from '../helpers/outlookHelper'

export type ResolvedTheme = 'light' | 'dark'

export type ResolvedThemeResult = {
    themeOption: ThemeOption
    resolvedTheme: ResolvedTheme
    setThemeOption: (option: ThemeOption) => void
}

/**
 * Single source of truth for the app theme: combines the manual Light/Dark/Auto choice
 * (localStorage key 'currentTheme', synced across hook instances) with the OS color scheme
 * and the Office host theme. Style theme-dependent custom colors off `resolvedTheme` (or
 * Fluent tokens) instead of `@media (prefers-color-scheme: dark)` — the media query only
 * tracks the OS setting and ignores the in-app toggle.
 */
export default function useResolvedTheme(): ResolvedThemeResult {
    const { values: storedThemeOption, setValues: setThemeOption } = useLocalStorage<ThemeOption>({
        key: 'currentTheme',
        defaultValue: ThemeOption.Auto,
        fromJson: values => values,
        toJson: values => values,
    })

    // Track the system's preferred color scheme so that the app responds live when the user toggles their OS theme while in Auto mode.
    const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => !!window.matchMedia?.('(prefers-color-scheme: dark)')?.matches)
    useEffect(() => {
        const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)')
        if (!mediaQuery) {
            return undefined
        }
        const handler = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches)
        mediaQuery.addEventListener('change', handler)
        return () => mediaQuery.removeEventListener('change', handler)
    }, [])

    const themeOption = storedThemeOption ?? ThemeOption.Auto

    const resolvedTheme = useMemo<ResolvedTheme>(() => {
        if (themeOption === ThemeOption.Auto) {
            if (isOfficeInitialized() && Office.context.officeTheme.isDarkTheme === true) {
                return 'dark'
            }
            return systemPrefersDark ? 'dark' : 'light'
        }
        return themeOption === ThemeOption.Dark ? 'dark' : 'light'
    }, [themeOption, systemPrefersDark])

    return { themeOption, resolvedTheme, setThemeOption }
}
